import { createContext, Dispatch, PropsWithChildren, useReducer } from 'react';

import { readStorage, writeStorage } from 'utils/storage';

import { useCostOverTime } from '../../../../../../cost-report/hooks/useCostOverTime';
import {
  CostReportPodTab,
  PodCostMode,
  PodCountMode,
} from '../../../../../../cost-report/types/costOverTime';

type ReducerState = {
  activeTab: CostReportPodTab;
  activeCountTab: PodCountMode;
  activeCostTab: PodCostMode;
};

type ReducerAction =
  | { type: 'changeActiveTab'; activeTab: CostReportPodTab }
  | { type: 'changeCountActiveTab'; activeTab: PodCountMode }
  | { type: 'changeCostActiveTab'; activeTab: PodCostMode };

type Props = PropsWithChildren<unknown>;

const makeInitialState = (storageKey: string): ReducerState => ({
  activeTab: readStorage(`${storageKey}.active-tab`, CostReportPodTab.COUNT),
  activeCountTab: readStorage(
    `${storageKey}.active-count-tab`,
    PodCountMode.TOTAL
  ),
  activeCostTab: readStorage(
    `${storageKey}.active-cost-tab`,
    PodCostMode.NORMALIZED
  ),
});

type PodMetricsContextValue = [ReducerState, Dispatch<ReducerAction>];

const PodMetricsContext = createContext<PodMetricsContextValue>([] as never);

const makeReducerFn =
  (storageKey: string) => (state: ReducerState, action: ReducerAction) => {
    switch (action.type) {
      case 'changeActiveTab': {
        writeStorage(`${storageKey}.active-tab`, action.activeTab);
        return {
          ...state,
          activeTab: action.activeTab,
        };
      }
      case 'changeCountActiveTab': {
        writeStorage(`${storageKey}.active-count-tab`, action.activeTab);
        return {
          ...state,
          activeCountTab: action.activeTab,
        };
      }
      case 'changeCostActiveTab': {
        writeStorage(`${storageKey}.active-cost-tab`, action.activeTab);
        return {
          ...state,
          activeCostTab: action.activeTab,
        };
      }
    }
  };

export const PodMetricsProvider = ({ children }: Props) => {
  const { key } = useCostOverTime();
  const STORAGE_KEY = `cost-report.${key}.pod`;
  const [state, dispatch] = useReducer(
    makeReducerFn(STORAGE_KEY),
    makeInitialState(STORAGE_KEY)
  );

  return (
    <PodMetricsContext.Provider value={[state, dispatch]}>
      {children}
    </PodMetricsContext.Provider>
  );
};
